/**
 * `wayai bases tokens …` — scoped API tokens for the Data surface.
 *
 * Namespaced because WayAI's own token surface mints `way_` platform tokens;
 * these mint `rec_` tokens scoped to base data.
 *
 * **The raw token is shown exactly once, on the create paths, and nowhere else.**
 * `list` and `prune` read metadata only, and `printCreatedToken` is fail-closed:
 * it validates the shape before printing rather than letting a missing field
 * become the string "undefined". No path here writes a token to disk or folds
 * one into an error message.
 */

import { Command } from 'commander';
import { createDataClient, dataErrorDetails, type DataClient } from '../client.js';
import { printOutput, type OutputFormat } from '../output.js';
import {
  isInteractive,
  outputFormat,
  parseData,
  parseDuration,
  pathSegment,
  requireBase,
  toolsetMcpUrl,
  withBaseOption,
} from '../helpers.js';
import { ApiError } from '../../lib/api-client.js';
import { sanitizeTerminalText } from '../../lib/terminal-output.js';
import { confirm } from '../../lib/utils.js';
import { expected } from '../../lib/expected.js';

interface TokenLifecycleOpts {
  expiresAt?: string;
  ttl?: string;
  description?: string;
}

interface TokenCreationOpts extends TokenLifecycleOpts {
  tokenOnly?: boolean;
}

type TokenCreationOutput = OutputFormat | 'token';

// A minted token is `rec_` plus 32 random bytes as unpadded base64url (43 chars).
const RAW_TOKEN_PATTERN = /^rec_[A-Za-z0-9_-]{43}$/;

/**
 * The raw `rec_` token a creation answered with. Fail-closed: a missing or malformed field throws,
 * and the error says nothing about what WAS returned, since it reaches the console and Sentry and
 * the field it inspected is the credential itself.
 */
export function rawTokenFrom(data: unknown): string {
  const token = typeof data === 'object' && data !== null && 'token' in data ? data.token : undefined;
  if (typeof token !== 'string' || !RAW_TOKEN_PATTERN.test(token)) {
    throw new Error('Token creation response did not contain a valid rec_ token');
  }
  return token;
}

const TOKEN_OUTPUT_HELP = `
Output:
  --json prints the unwrapped response object; the secret is at top-level .token, never .data.token.
  --token-only prints only a validated rec_ token and overrides table/JSON formatting.
  The raw token is shown once. Capture it securely and do not log it.
`;

/** Creation lifecycle/output options shared by `create` and `create-for-toolset`. */
function addTokenLifecycleOptions(cmd: Command): Command {
  return cmd
    .option('--expires-at <date>', 'Expiration date (ISO 8601, e.g. 2026-06-01T00:00:00Z)')
    .option(
      '--ttl <duration>',
      'Relative expiry, e.g. 10m, 2h, 7d (use for disposable probe/test tokens)',
    )
    .option(
      '--description <text>',
      'What holds this token (e.g. "behind credential X"); shown in list and revoke warnings',
    )
    .addHelpText('after', TOKEN_OUTPUT_HELP);
}

function tokenCreationOutput(cmd: Command, opts: TokenCreationOpts): TokenCreationOutput {
  return opts.tokenOnly ? 'token' : outputFormat(cmd);
}

/** Raw output is intentionally fail-closed: never turn a missing field into "null". */
function printCreatedToken(data: unknown, output: TokenCreationOutput): void {
  if (output !== 'token') {
    printOutput(data, output);
    return;
  }
  console.log(rawTokenFrom(data));
}

/** `--ttl` is sugar for a relative `expires_at`; the server only ever sees the ISO date. */
function resolveExpiresAt(opts: TokenLifecycleOpts): string | undefined {
  if (opts.ttl && opts.expiresAt) {
    throw expected('--ttl and --expires-at are mutually exclusive — pass one.');
  }
  if (opts.ttl) return new Date(Date.now() + parseDuration(opts.ttl, '--ttl')).toISOString();
  return opts.expiresAt;
}

/** Fold the resolved expiry + description onto a create request body. */
function applyTokenLifecycleOpts(body: Record<string, unknown>, opts: TokenLifecycleOpts): void {
  const expiresAt = resolveExpiresAt(opts);
  if (expiresAt) body.expires_at = expiresAt;
  if (opts.description) body.description = opts.description;
}

interface TokenListItem {
  token_id: string;
  name: string;
  created_at: string;
  status?: 'active' | 'expired';
  usage_tracked?: boolean;
  last_used_at?: string;
  expires_at?: string;
  description?: string;
  toolset_binding?: { base_id: string; toolset_id: string };
}

// The token list is a bounded page plus a cursor — follow it so `list`/`prune`
// still see the whole fleet. The max page size minimizes round-trips; the server
// bounds its own reads per page, so a large fleet costs several sequential
// requests rather than one failure.
const TOKEN_LIST_PAGE_LIMIT = 250;

function listAllTokens(client: DataClient): Promise<TokenListItem[]> {
  return client.collectPages<TokenListItem>(
    (cursor) =>
      `/v1/tokens?limit=${TOKEN_LIST_PAGE_LIMIT}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`,
  );
}

interface PruneRow {
  token_id: string;
  name: string;
  reason: string;
  last_used: string;
  [key: string]: string;
}

function pruneRow(t: TokenListItem, reason: string): PruneRow {
  return {
    token_id: t.token_id,
    name: t.name,
    reason,
    last_used: t.last_used_at ?? 'never',
  };
}

export function buildBasesTokensCommand(): Command {
  const tokens = new Command('tokens').description('Manage API tokens');

  addTokenLifecycleOptions(
    tokens
      .command('create')
      .description('Create a scoped API token')
      .requiredOption('--name <name>', 'Token name')
      .requiredOption('--grants <json>', 'Grant definitions as JSON array')
      .option(
        '--token-only',
        'Print only the raw rec_ token for secure command substitution (shown once)',
      ),
  ).action(async function (
    this: Command,
    opts: TokenCreationOpts & { name: string; grants: string },
  ) {
    // `parseData`, so `--grants @grants.json` works like every other JSON option
    // on this surface — a grant array is exactly the thing kept in a file. It
    // tags and labels its own failure, so no wrapper here.
    const grants: unknown = parseData(opts.grants, '--grants');
    const body: Record<string, unknown> = { name: opts.name, grants };
    applyTokenLifecycleOpts(body, opts);
    const client = await createDataClient();
    const data = await client.request('POST', '/v1/tokens', body);
    printCreatedToken(data, tokenCreationOutput(this, opts));
  });

  addTokenLifecycleOptions(
    // The only token verb that addresses a base, so it alone declares `--base`.
    // Putting it on the `tokens` group would advertise a flag that does nothing
    // on `list`, `revoke` and `prune` — the defect `withBaseOption` exists to
    // avoid.
    withBaseOption(tokens.command('create-for-toolset <slug>'))
      .description(
        'Create a toolset-bound (consumer) token whose authorization IS the toolset tool surface — it can call exactly the tools that toolset exposes (those record types + operations, relationships, batch, and sql only if enabled) and nothing else. Binds to the configured --base.',
      )
      .option('--name <name>', 'Token name (default: "<slug> consumer")')
      .option(
        '--token-only',
        'Print only the raw rec_ token for secure command substitution (shown once)',
      ),
  ).action(async function (this: Command, slug: string, opts: TokenCreationOpts & { name?: string }) {
    const base = requireBase(this);
    const body: Record<string, unknown> = {
      name: opts.name ?? `${slug} consumer`,
      toolset_binding: { base_id: base, toolset_id: slug },
    };
    applyTokenLifecycleOpts(body, opts);
    const client = await createDataClient();
    const data = await client.request('POST', '/v1/tokens', body);
    const output = tokenCreationOutput(this, opts);
    printCreatedToken(data, output);
    // "Here is the server you just created, its token, and its URL." Only in the
    // human format — `--json` and `--token-only` stay machine-parseable.
    if (output === 'table') {
      console.log(`\nMCP URL: ${toolsetMcpUrl(slug)}`);
    }
  });

  tokens
    .command('list')
    .description('List API tokens (status, last_used_at, expires_at, description)')
    .action(async function (this: Command) {
      const client = await createDataClient();
      printOutput(await listAllTokens(client), outputFormat(this));
    });

  tokens
    .command('revoke <token_id>')
    .description(
      'Revoke an API token (permanent). A recently-used or toolset-bound token requires --force.',
    )
    .option('--force', 'Revoke even if the token appears to be in live use')
    .action(async function (this: Command, tokenId: string, opts: { force?: boolean }) {
      const client = await createDataClient();
      const id = pathSegment(tokenId, 'token id');
      const path = (force: boolean) => `/v1/tokens/${id}${force ? '?force=true' : ''}`;
      // Honors --json: a script revoking a fleet reads the outcome off stdout.
      const format = outputFormat(this);
      const revoked = (forced: boolean) =>
        printOutput({ token_id: tokenId, revoked: true, forced }, format);
      if (opts.force) {
        await client.request('DELETE', path(true));
        revoked(true);
        return;
      }
      try {
        await client.request('DELETE', path(false));
        revoked(false);
      } catch (err) {
        if (
          !(err instanceof ApiError) ||
          err.status !== 409 ||
          dataErrorDetails(err)?.requires_force !== true
        ) {
          throw err;
        }
        // The token looks live (recently used and/or toolset-bound). Require an
        // explicit decision, and never auto-confirm off a TTY: agents run
        // non-interactive, and a batch revoke of live consumer tokens is exactly
        // the incident this guard exists to stop.
        console.error(err.message);
        if (!isInteractive()) {
          console.error('\nRe-run with --force to revoke anyway.');
          return process.exit(1);
        }
        if (!(await confirm('Revoke anyway?'))) {
          console.error('Aborted.');
          return process.exit(1);
        }
        await client.request('DELETE', path(true));
        revoked(true);
      }
    });

  tokens
    .command('prune')
    .description(
      'Revoke stale tokens, selected by staleness only (expired, or unused past --unused-since) — never by name. Previews by default; revoking requires --yes or an interactive confirmation.',
    )
    .option(
      '--unused-since <duration>',
      'Also prune tokens with no activity for this long (e.g. 30d). Skips toolset-bound and legacy usage-untracked tokens.',
    )
    .option('--include-bound', 'Allow pruning stale toolset-bound (consumer) tokens')
    .option('-y, --yes', 'Revoke without prompting (required when non-interactive)')
    .action(async function (
      this: Command,
      opts: { unusedSince?: string; includeBound?: boolean; yes?: boolean },
    ) {
      const staleMs = opts.unusedSince ? parseDuration(opts.unusedSince, '--unused-since') : undefined;
      const client = await createDataClient();
      const allTokens = await listAllTokens(client);
      const now = Date.now();

      const targets: PruneRow[] = [];
      const skipped: PruneRow[] = [];
      for (const t of allTokens) {
        const expired = t.status
          ? t.status === 'expired'
          : !!t.expires_at && Date.parse(t.expires_at) <= now;
        if (expired) {
          // Expiry is authoritative — an expired token is dead regardless of
          // binding or usage tracking.
          targets.push(pruneRow(t, 'expired'));
          continue;
        }
        if (staleMs === undefined) continue;
        const lastActivity = Date.parse(t.last_used_at ?? t.created_at);
        if (!(now - lastActivity > staleMs)) continue;
        if (t.usage_tracked !== true) {
          // Legacy tokens never record last_used_at, so "stale" is unknowable —
          // a live one would look never-used. Never prunable via staleness.
          //
          // `!== true`, NOT `=== false`: the field is optional, so ABSENT is a
          // state the type models, and it means the same thing as `false` —
          // usage was not tracked. Testing `=== false` failed OPEN on it: a row
          // with neither `usage_tracked` nor `last_used_at` dates from
          // `created_at`, passes the staleness test, misses this guard, and is
          // permanently revoked with `?force=true`. Only a POSITIVE claim that
          // usage IS tracked may let a token through on staleness alone.
          skipped.push(
            pruneRow(
              t,
              'usage untracked (legacy token) — revoke individually if truly unused',
            ),
          );
        } else if (t.toolset_binding && !opts.includeBound) {
          skipped.push(
            pruneRow(
              t,
              `toolset-bound ('${t.toolset_binding.toolset_id}') — pass --include-bound to prune`,
            ),
          );
        } else {
          targets.push(pruneRow(t, `unused for over ${opts.unusedSince}`));
        }
      }

      // Under `--json` stdout carries exactly ONE document — the preview, or the
      // result. The same listing still reaches a human, on stderr, so an
      // operator confirming a destructive prune sees what they are confirming
      // without a `jq` consumer having to skip past it.
      const format = outputFormat(this);
      const json = format === 'json';
      const note = (line: string) => (json ? console.error(line) : console.log(line));
      const printPreview = () => {
        if (targets.length === 0) {
          note('Nothing to prune.');
        } else {
          note(`Would revoke ${targets.length} token(s):`);
          // NOT under --json: `printOutput` renders through `printRows`, which
          // writes with `console.log` for BOTH formats — so rendering the
          // preview "as a table" here still put it on stdout, ahead of the JSON
          // document, and `| jq` failed on every run that had anything to
          // report. The document below already carries `targets` and `skipped`
          // in full, so the machine path loses nothing by skipping the table.
          if (!json) printOutput(targets, format);
        }
        if (skipped.length === 0) return;
        note(`\nSkipped ${skipped.length} stale token(s):`);
        if (!json) printOutput(skipped, format);
      };
      printPreview();

      if (targets.length === 0) {
        if (json) printOutput({ revoked: 0, failed: 0, targets, skipped }, format);
        return;
      }

      if (!opts.yes) {
        if (!isInteractive()) {
          console.error('\nPreview only — re-run with --yes to revoke these tokens.');
          if (json) printOutput({ preview: true, revoked: 0, failed: 0, targets, skipped }, format);
          return process.exit(1);
        }
        if (!(await confirm(`\nRevoke ${targets.length} token(s)?`))) {
          console.error('Aborted.');
          return process.exit(1);
        }
      }

      let revoked = 0;
      let failed = 0;
      for (const t of targets) {
        try {
          // force=true: the prune was already confirmed above, and expired
          // targets are dead anyway.
          await client.request(
            'DELETE',
            `/v1/tokens/${pathSegment(t.token_id, 'token id')}?force=true`,
          );
          revoked++;
        } catch (err) {
          failed++;
          // The token name is user-authored and this line is built by hand, so
          // it is sanitized here rather than by the table renderer.
          console.error(
            `Failed to revoke ${t.token_id} ('${sanitizeTerminalText(t.name)}'): ${err instanceof Error ? err.message : String(err)}`,
          );
        }
      }
      if (json) printOutput({ revoked, failed, targets, skipped }, format);
      else console.log(`Revoked ${revoked} token(s)${failed > 0 ? `, ${failed} failed` : ''}.`);
      if (failed > 0) return process.exit(1);
    });

  return tokens;
}
